import React from "react";
import { ImageInfoGalleryItem } from "@glamcorner/gc-types/generated/types";
import { microDataAttributeGenerator } from "../microDataAttribute";
import "./image.scss";

const smallImageWidth = 177;
const largeImageWidth = 262;

interface ImageProps {
  label: string | null;
  imageInfoGalleryItem: ImageInfoGalleryItem;
  camelCaseMicroData?: boolean;
  eagerLoad?: boolean;
}

interface SrcSetData {
  width: number;
  url: string;
}

export const Image: React.FC<ImageProps> = ({ label, imageInfoGalleryItem, camelCaseMicroData = true, eagerLoad }) => {
  const microdata = microDataAttributeGenerator(!!camelCaseMicroData);

  const srcSetSizes = imageSizes(imageInfoGalleryItem);
  const srcSet = imageSizesToSrcSet(srcSetSizes);
  const sizes = buildSizes();

  return (
    <div data-rules={imageInfoGalleryItem.visibility_rules}>
      <img
        className="c-image"
        src={srcSetSizes[0].url}
        srcSet={srcSet}
        alt={label || ""}
        {...microdata("itemProp", "image")}
        sizes={sizes}
        loading={eagerLoad ? "eager" : "lazy"}
      />
    </div>
  );
};

function imageSizesToSrcSet(data: SrcSetData[]): string {
  return data.map(({ width, url }) => `${url} ${width}w`).join(", ");
}

function buildSizes(): string {
  return [
    "(min-width: 543px) 262px" /* when doc is this width or greater, the images are always 262px wide */,
    "48vw" /* when screen is small / mobile, the images are always 48% of the width of the screen */,
  ].join(", ");
}

function imageSizes(imageInfoGalleryItem: ImageInfoGalleryItem): SrcSetData[] {
  const results: SrcSetData[] = [];

  const imageSizeNames: ("small" | "large")[] = ["small", "large"];
  const imageWidths = {
    small: smallImageWidth,
    large: largeImageWidth,
  };
  const scales: { scale: number; key: "x1" | "x1_5" | "x2" | "x3" }[] = [
    { scale: 1, key: "x1" },
    { scale: 1.5, key: "x1_5" },
    { scale: 2, key: "x2" },
    { scale: 3, key: "x3" },
  ];
  if (imageInfoGalleryItem.url_by_size_and_pixel_density) {
    imageSizeNames.forEach((size) => {
      scales.forEach(({ scale, key }) => {
        const width = Math.floor(imageWidths[size] * scale);
        const url = imageInfoGalleryItem.url_by_size_and_pixel_density[size]![key]!;
        results.push({ width, url });
      });
    });
  } else if (imageInfoGalleryItem.url_by_pixel_density) {
    results.push({ width: 262, url: imageInfoGalleryItem.url_by_pixel_density.x1 });
  }

  return results.sort((a, b) => {
    if (a.width < b.width) {
      return -1;
    } else if (a.width > b.width) {
      return 1;
    } else {
      return 0;
    }
  });
}
